import {
  ChangeDetectionStrategy, Component, inject, signal, OnInit, PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { ToastService } from '../../../core/services/toast.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import { TrimWhitespaceDirective } from '../../../core/directives/trim.directive';
import { trimFormGroup } from '../../../core/utils/form.utils';

const passwordMatchValidator: ValidatorFn = (control: AbstractControl) => {
  const newPassword = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');
  if (!newPassword || !confirmPassword) return null;
  return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
};

type PageState = 'verifying' | 'ready' | 'error' | 'success';

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.html',
  styleUrl: './reset-password.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, TrimWhitespaceDirective],
  host: { ngSkipHydration: 'true' },
})
export class ResetPassword implements OnInit {
  private fb           = inject(FormBuilder);
  private apiService   = inject(ApiService);
  private toastService = inject(ToastService);
  private route        = inject(ActivatedRoute);
  private router       = inject(Router);
  private platformId   = inject(PLATFORM_ID);

  readonly pageState    = signal<PageState>('verifying');
  readonly errorMessage = signal('');
  readonly submitError  = signal('');
  readonly isSubmitting = signal(false);
  readonly showPassword = signal(false);
  readonly showConfirmPassword = signal(false);

  private resetToken = '';

  resetForm = this.fb.group(
    {
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(12),
          Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/),
        ],
      ],
      confirmPassword: ['', Validators.required],
    },
    { validators: passwordMatchValidator }
  );

  get f() { return this.resetForm.controls; }
  get passwordMismatch(): boolean {
    return !!this.resetForm.errors?.['passwordMismatch'] && !!this.f['confirmPassword'].touched;
  }
  get passwordCriteria() {
    const val: string = this.f['newPassword'].value || '';
    return {
      minLength:  val.length >= 8,
      maxLength:  val.length <= 12 && val.length > 0,
      hasUpper:   /[A-Z]/.test(val),
      hasNumber:  /[0-9]/.test(val),
      hasSpecial: /[@$!%*?&]/.test(val),
    };
  }

  togglePassword():        void { this.showPassword.update(v => !v); }
  toggleConfirmPassword(): void { this.showConfirmPassword.update(v => !v); }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.resetToken = this.route.snapshot.queryParamMap.get('token') ?? '';

    if (!this.resetToken) {
      this.errorMessage.set('Invalid or missing reset link. Please request a new one.');
      this.pageState.set('error');
      this.redirectToLoginAfterDelay();
      return;
    }

    this.verifyToken();
  }

  private async verifyToken(): Promise<void> {
    try {
      // type = 2 → PasswordReset
      const resp = await this.apiService.request<string>(
        'POST',
        API_ROUTES.VERIFY_TOKEN,
        { token: this.resetToken, type: 2 },
        { useToken: false, showToaster: false, showLoader: false }
      );
      if (resp.status) {
        this.pageState.set('ready');
      } else {
        this.errorMessage.set(resp.message || 'This reset link is invalid or has expired.');
        this.pageState.set('error');
        this.redirectToLoginAfterDelay();
      }
    } catch {
      this.errorMessage.set('Something went wrong while validating your reset link.');
      this.pageState.set('error');
      this.redirectToLoginAfterDelay();
    }
  }

  async onSubmit(): Promise<void> {
    trimFormGroup(this.resetForm);
    this.resetForm.markAllAsTouched();
    if (this.resetForm.invalid) return;

    this.submitError.set('');
    this.isSubmitting.set(true);
    const { newPassword } = this.resetForm.value;

    try {
      const resp = await this.apiService.request<string>(
        'POST',
        API_ROUTES.RESET_PASSWORD,
        { token: this.resetToken, newPassword },
        { useToken: false, showToaster: false }
      );

      if (resp.status) {
        this.pageState.set('success');
        this.toastService.success(resp.message || 'Password reset successfully!');
        this.redirectToLoginAfterDelay();
      } else {
        this.submitError.set(resp.message || 'Failed to reset password. Please try again.');
      }
    } catch {
      this.submitError.set('Something went wrong. Please try again.');
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private redirectToLoginAfterDelay(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    setTimeout(() => this.router.navigate(['/login']), 3000);
  }
}
